import {
	useCallback,
	useEffect,
	useRef,
} from 'react';
import {
	Currencies,
	VesselCostType,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { calculateTotal } from '@shared/utils/math';
import type { EstimateCostProps } from '@api/models/estimate-cost';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import {
	addEstimateCostToEstimate,
	updateEstimateCostToEstimate,
} from '@client/lib/api';

export const calcTotal = (costs?: EstimateCostProps[]) => {
	return calculateTotal(costs ?? [], (c) => c.amount) ?? 0;
};

export const saveMiscCost = async ({
	estimateId,
	currency,
	estimateCosts,
	value,
}: {
	estimateId: number;
	currency: Values<typeof Currencies>;
	estimateCosts: EstimateCostProps[] | undefined;
	value: number;
}) => {
	const costAttributes = {
		estimateId,
		unitType: VesselCostType.Lumpsum,
		units: 1,
		unitPrice: value,
		localAmount: value,
		currency,
		exchangeRate: 1,
		amount: value, // base = local * 1
	};

	if (estimateCosts != null && estimateCosts.length === 1) {
		const existing = estimateCosts[0];
		await updateEstimateCostToEstimate(existing.id, estimateId, {
			...costAttributes,
			id: existing.id,
			description: existing.description,
		});

		return;
	}

	await addEstimateCostToEstimate({
		...costAttributes,
		description: 'Misc.',
	});
};

export const useMiscEstimateCostHandler = ({
	estimate,
	estimateCosts,
	refreshEstimateCosts,
	onUpdateEstimate,
}: {
	estimate: GetEstimateDetailsResponse | null;
	estimateCosts: EstimateCostProps[] | undefined;
	refreshEstimateCosts: () => Promise<any>;
	onUpdateEstimate: (data: Partial<GetEstimateDetailsResponse>) => void;
}) => {
	const latestValue = useRef<number | null>(null);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const save = useCallback(async (value: number) => {
		if (!estimate) {
			return;
		}

		try {
			await saveMiscCost({
				estimateId: estimate.id,
				currency: estimate.currency,
				estimateCosts,
				value,
			});
			await refreshEstimateCosts();
		} catch (err) {
			console.error('Failed to save misc cost:', err);
		}
	}, [estimate, estimateCosts, refreshEstimateCosts]);

	const handleMiscCostChange = useCallback((value: number) => {
		if (!estimate) {
			return;
		}

		latestValue.current = value;
		onUpdateEstimate({ miscCostTotal: value });

		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		timerRef.current = setTimeout(() => {
			if (latestValue.current != null) {
				save(latestValue.current);
			}
		}, 600);
	}, [estimate, onUpdateEstimate, save]);

	useEffect(() => () => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}
	}, []);

	return { handleMiscCostChange };
};
